// src/components/communication/NewTaskModal.js
import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  AlertCircle,
  Flag,
  User,
  Wrench,
  Package,
  CheckCircle2,
  Clock,
  Layers,
  Users,
} from "lucide-react";
import { useCommunication } from "../../contexts/CommunicationContext";
import { colaboradoresService } from "../../services/colaboradoresService";

const PRIORITY_OPTIONS = [
  { value: "urgente", label: "Urgente", color: "bg-rose-500 text-white border-rose-600", desc: "Ação no mesmo dia" },
  { value: "alta", label: "Alta", color: "bg-orange-500 text-white border-orange-600", desc: "Prazo de 24h a 48h" },
  { value: "media", label: "Média", color: "bg-amber-500 text-white border-amber-600", desc: "Prazo regular" },
  { value: "baixa", label: "Baixa", color: "bg-emerald-500 text-white border-emerald-600", desc: "Sem urgência" },
];

export default function NewTaskModal() {
  const {
    newTaskModalOpen,
    setNewTaskModalOpen,
    createTask,
    usersList,
    operatorsList,
  } = useCommunication();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("media");
  const [assigneeType, setAssigneeType] = useState("user"); // "user" | "operator" | "team"
  const [assignedId, setAssignedId] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [moduleKey, setModuleKey] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [teamsList, setTeamsList] = useState([]);

  useEffect(() => {
    if (newTaskModalOpen) {
      colaboradoresService
        .fetchEquipes()
        .then((list) => setTeamsList(list || []))
        .catch(() => setTeamsList([]));
    }
  }, [newTaskModalOpen]);

  if (!newTaskModalOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Por favor, preencha o título da tarefa.");
      return;
    }

    if (assigneeType === "team" && !assignedId) {
      setError("Por favor, selecione qual equipe/time receberá a tarefa.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const selectedTeam = assigneeType === "team" ? teamsList.find((t) => t.id === assignedId) : null;

      await createTask({
        title: title.trim(),
        description: description.trim(),
        priority,
        assigned_user: assigneeType === "user" ? assignedId || null : null,
        assigned_operator: assigneeType === "operator" ? assignedId || null : null,
        assigned_team: assigneeType === "team" ? assignedId || null : null,
        team_name: selectedTeam ? selectedTeam.nome : null,
        order_number: orderNumber ? orderNumber.replace(/[[\]#]/g, "").trim() : null,
        module_key: moduleKey || null,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
      });

      // Reset
      setTitle("");
      setDescription("");
      setPriority("media");
      setAssigneeType("user");
      setAssignedId("");
      setOrderNumber("");
      setDueDate("");
      setNewTaskModalOpen(false);
    } catch (err) {
      console.error("Erro ao criar tarefa:", err);
      setError("Falha ao salvar a tarefa. Verifique os dados.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      <div
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] animate-scaleUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 bg-slate-900 text-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center text-amber-400">
              <Calendar size={20} />
            </div>
            <div>
              <h3 className="font-bold text-base text-white">Nova Tarefa / Atribuição</h3>
              <p className="text-xs text-slate-400">Defina prioridade, prazos e responsável ou equipe</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setNewTaskModalOpen(false)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* FORMULÁRIO */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 flex items-center gap-2 text-xs">
              <AlertCircle size={16} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* TÍTULO */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
              Título da Tarefa *
            </label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Ajustar garras do anel de formatura antes do banho"
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-2xs"
            />
          </div>

          {/* DESCRIÇÃO */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
              Instruções / Detalhes (Opcional)
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Adicione observações, passos a seguir ou critérios de aprovação..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-2xs resize-none"
            />
          </div>

          {/* PRIORIDADE */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <Flag size={13} />
              Nível de Prioridade
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PRIORITY_OPTIONS.map((opt) => {
                const isSelected = priority === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPriority(opt.value)}
                    className={`p-2 rounded-xl border text-center transition flex flex-col items-center gap-1 cursor-pointer ${
                      isSelected
                        ? `${opt.color} shadow-xs font-bold`
                        : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    <span className="text-xs font-bold">{opt.label}</span>
                    <span className={`text-[10px] ${isSelected ? "text-white/80" : "text-slate-400"}`}>
                      {opt.desc}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ATRIBUIÇÃO (USUÁRIO / OPERADOR / TIME) */}
          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                {assigneeType === "team" ? (
                  <Users size={14} className="text-purple-600" />
                ) : assigneeType === "operator" ? (
                  <Wrench size={13} className="text-blue-600" />
                ) : (
                  <User size={13} className="text-slate-600" />
                )}
                Atribuir para:
              </label>

              {/* SELETOR DO TIPO DE DESTINO */}
              <div className="flex rounded-lg bg-slate-200 p-0.5 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => {
                    setAssigneeType("user");
                    setAssignedId("");
                  }}
                  className={`px-2.5 py-1 rounded-md transition cursor-pointer ${
                    assigneeType === "user" ? "bg-white text-slate-800 shadow-xs" : "text-slate-500"
                  }`}
                >
                  Usuário
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAssigneeType("operator");
                    setAssignedId("");
                  }}
                  className={`px-2.5 py-1 rounded-md transition cursor-pointer ${
                    assigneeType === "operator" ? "bg-white text-slate-800 shadow-xs" : "text-slate-500"
                  }`}
                >
                  Operador
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAssigneeType("team");
                    setAssignedId("");
                  }}
                  className={`px-2.5 py-1 rounded-md transition cursor-pointer flex items-center gap-1 ${
                    assigneeType === "team" ? "bg-purple-600 text-white shadow-xs" : "text-purple-700 hover:text-purple-900"
                  }`}
                >
                  <Users size={12} />
                  <span>Time / Equipe</span>
                </button>
              </div>
            </div>

            {assigneeType === "user" ? (
              <select
                value={assignedId}
                onChange={(e) => setAssignedId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Selecione um usuário individual (opcional)</option>
                {usersList.map((u) => (
                  <option key={u.id} value={u.id}>
                    👤 {u.name || u.username} ({u.role || "user"})
                  </option>
                ))}
              </select>
            ) : assigneeType === "operator" ? (
              <select
                value={assignedId}
                onChange={(e) => setAssignedId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Selecione um operador de bancada/PDV (opcional)</option>
                {operatorsList.map((op) => (
                  <option key={op.id} value={op.id}>
                    🔧 {op.nome} — {op.tipo || "Oficina"}
                  </option>
                ))}
              </select>
            ) : (
              <div className="space-y-1.5">
                <select
                  value={assignedId}
                  onChange={(e) => setAssignedId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-purple-300 text-xs font-bold bg-white text-purple-950 focus:outline-none focus:ring-2 focus:ring-purple-500 shadow-2xs"
                >
                  <option value="">Selecione qual equipe/time receberá a tarefa...</option>
                  {teamsList.map((team) => (
                    <option key={team.id} value={team.id}>
                      👥 {team.nome} {team.descricao ? `— ${team.descricao}` : ""}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-purple-700 font-medium">
                  ✨ Todos os membros e operadores desta equipe receberão aviso no seu <b>chat pessoal (DM)</b> e poderão concluir a tarefa coletivamente.
                </p>
              </div>
            )}
          </div>

          {/* VÍNCULO COM PEDIDO E DATA LIMITE */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Package size={13} />
                Nº do Pedido (Opcional)
              </label>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="Ex: 71740"
                className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs font-mono font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-2xs"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Clock size={13} />
                Prazo / Data Limite (Opcional)
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-2xs"
              />
            </div>
          </div>

          {/* BOTÕES DE AÇÃO */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={() => setNewTaskModalOpen(false)}
              className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-sm shadow-indigo-600/30 transition disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
            >
              {loading ? "Criando..." : "Criar Tarefa"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
